import { Input as AntdInput } from 'antd';
import React, { ChangeEvent, FC, KeyboardEvent, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { ProductiveHeading06 } from 'components/styles';
import { AccountProvider, CurrentUserProvider } from 'contexts/providers';
import { DocumentResourceType } from 'data/documents';
import { useStatusField } from 'data/fields/hooks';
import {
  createDocumentTemplateResourceSectionAction,
  createDocumentTemplateTaskAction,
  deleteDocumentTemplateTaskAction,
  selectResourceSectionById,
  updateDocumentTemplateResourceSectionAction,
  updateDocumentTemplateTaskAction,
} from 'data/templates/documents';
import { useDocumentById } from 'data/templates/documents/hooks/use-document-by-id';
import { ProjectTemplateTask } from 'data/templates/projects';
import { useTranslation } from 'hooks/use-translation';
import { DocumentTemplateTaskList } from '../task-list';

const Input = styled(AntdInput)`
  ${ProductiveHeading06};
  background: none;
  margin: var(--layout-01) 0;

  &,
  &:focus {
    border: none;
    outline: none;
    box-shadow: none;
  }
`;

export const TemplateTaskSection: FC<{
  docId: number;
  resourceSectionId: number;
  sectionId: number;
  readOnly?: boolean;
}> = ({ docId, resourceSectionId, sectionId, readOnly }) => {
  const defaultSectionTitle = useTranslation(
    'editor.doc_task_section_placeholder',
  );
  const dispatch = useDispatch();
  const documentTemplate = useDocumentById(docId);
  const statusField = useStatusField();

  const resourceSection = useSelector(
    selectResourceSectionById(
      documentTemplate.templateId as number,
      resourceSectionId,
    ),
  );

  const resourceSectionName =
    resourceSection?.resourceSectionName ||
    resourceSection?.resourceSectionName === ''
      ? resourceSection?.resourceSectionName
      : defaultSectionTitle;

  const data = resourceSection?.resourceObjects || [];

  const documentTitlePlaceholder = useTranslation(
    'editor.doc_task_section_placeholder',
  );

  const createResourceSection = useCallback(() => {
    dispatch(
      createDocumentTemplateResourceSectionAction({
        docId,
        resourceSectionId,
        resourceSectionName,
        resourceType: DocumentResourceType.Custom,
        sectionId,
      }),
    );
  }, [dispatch, docId, resourceSectionId, resourceSectionName, sectionId]);

  if (
    resourceSection?.resourceType === DocumentResourceType.Default ||
    resourceSection?.resourceType === DocumentResourceType.Highlight
  ) {
    return <></>;
  }

  const handleNewTaskAdded = (taskName: string) => {
    dispatch(
      createDocumentTemplateTaskAction({
        docId,
        task: {
          taskName,
          fields: [
            {
              fieldId: statusField.fieldId,
              fieldValue: statusField.metaData?.default as number,
            },
          ],
          private: sectionId === 0,
        },
        resourceSectionId,
      }),
    );

    return Promise.resolve();
  };

  const onSectionNameBlur = () => {
    const sectionName = resourceSectionName || defaultSectionTitle;

    if (!resourceSection?.resourceSectionId) {
      return;
    }

    dispatch(
      updateDocumentTemplateResourceSectionAction({
        docId,
        resource: {
          ...resourceSection,
          resourceSectionName: sectionName,
        },
      }),
    );
  };

  const onEnter = (e: KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    e.currentTarget.blur();
  };

  const onUpdateSectionTitle = (e: ChangeEvent<HTMLInputElement>) => {
    if (!resourceSection?.resourceSectionId) {
      return;
    }

    dispatch(
      updateDocumentTemplateResourceSectionAction({
        docId,
        resource: {
          ...resourceSection,
          resourceSectionName: e.target.value,
        },
      }),
    );
  };
  const handleAssigneeChange = (taskId, assignee) => {
    dispatch(
      updateDocumentTemplateTaskAction({
        docId,
        resourceSectionId,
        task: {
          taskId,
          assignee,
        },
      }),
    );
  };
  const onDeleteTask = (record: ProjectTemplateTask) => {
    dispatch(
      deleteDocumentTemplateTaskAction({
        docId,
        resourceSectionId,
        taskId: record.taskId,
      }),
    );
  };

  return (
    <AccountProvider>
      <CurrentUserProvider>
        {resourceSection?.resourceSectionId && (
          <Input
            placeholder={documentTitlePlaceholder}
            value={resourceSectionName}
            readOnly={readOnly}
            onChange={onUpdateSectionTitle}
            onBlur={onSectionNameBlur}
            onPressEnter={onEnter}
          />
        )}
        <DocumentTemplateTaskList
          isPrivate={sectionId === 0}
          data={data}
          docId={docId}
          resourceSectionId={resourceSectionId}
          readOnly={readOnly}
          handleAssigneeChange={handleAssigneeChange}
          handleNewTaskAdded={handleNewTaskAdded}
          onEnableTaskAddRow={createResourceSection}
          onDelete={onDeleteTask}
        />
      </CurrentUserProvider>
    </AccountProvider>
  );
};
